<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <!-- Kit Dialog (create kit type) -->
    <v-dialog v-model="kitDialog">
    </v-dialog>

    <!-- Create Kit Dialog -->
    <v-dialog v-model="createKitDialog" max-width="1200" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ createKitFormTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeCreateKitDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-form ref="createKitForm" v-model="createKitFormValidado">
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="createKitItem.id_tipo_kit"
                    :items="tiposKits"
                    item-text="nome_kit"
                    item-value="id_tipo_kit"
                    clearable
                    :label="$t('equipment.kit.selectKitType')"
                    :rules="[(v) => !!v || ($t('equipment.kit.kitTypeRequired'))]"
                    @change="onKitTypeSelected">
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="createKitItem.id_empresa"
                    :items="empresas"
                    item-text="razao_social"
                    item-value="id_empresa"
                    clearable
                    :label="$tc('company.self', 1)"
                    :rules="[(v) => !!v || ($t('equipment.companyRequired'))]">
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-switch v-model="createKitIsAtivo" class="ma-2" :label="$t('equipment.active')"></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete v-model="createKitItem.id_status_kit" :items="statusKits"
                    item-text="descricao_status_kit" item-value="id_status_kit"
                    :label="$t('equipment.status')"></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="createKitItem.id_veiculo"
                    :items="veiculosParaKit"
                    item-text="numero_ordem"
                    item-value="id_veiculo"
                    clearable
                    :label="$t('equipment.kit.vehicle')"
                    :hint="$t('equipment.kit.vehicleHint')"
                    persistent-hint>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.numero_ordem || item.cod_veiculo }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.placa">{{ item.placa }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="createKitItem.cod_kit"
                    :items="codKitAutocompleteItems"
                    item-text="codigo"
                    item-value="codigo"
                    :label="$t('equipment.kit.code')"
                    :rules="[createKitCodeRules]"
                    clearable
                    :filter="(item, queryText) => {
                      const code = item.codigo ? item.codigo.toLowerCase() : '';
                      const searchText = queryText.toLowerCase();
                      return code.includes(searchText);
                    }">
                    <template v-slot:append-outer>
                      <v-btn icon small @click="openKitQRScanner(editedKitIndex === -1 ? 'kit-create' : 'kit-edit')" color="primary">
                        <v-icon>mdi-qrcode-scan</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.codigo }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="createKitItem.nome_kit"
                    :label="$t('equipment.kit.kitNamePlaceholder')">
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Dynamic Equipment Type Sections -->
              <v-row v-if="selectedKitTypeEquipmentTypes.length > 0">
                <v-col cols="12">
                  <v-alert type="info" dense class="mb-4">
                    {{ $t('equipment.kit.selectEquipmentForType', { type: '' }) }}
                    <template v-if="equipmentTypesWithNoAvailableItems.length > 0">
                      <span class="d-block mt-2">{{ $t('equipment.kit.noDataAvailable') }}</span>
                      <span v-if="equipmentTypesWithNoAvailableItems.length > 0" class="text-caption d-block mt-1">
                        {{ equipmentTypesWithNoAvailableItems.map(et => et.nome_tipo_equipamento).join(', ') }}
                      </span>
                    </template>
                  </v-alert>

                  <v-card outlined v-for="equipType in selectedKitTypeEquipmentTypes" :key="equipType.id_tipo_equipamento" class="mb-4">
                    <v-card-title>
                      {{ equipType.nome_tipo_equipamento }}
                      <span v-if="equipType.quantidade != null" class="text-caption grey--text ml-2">({{ $t('equipment.kit.requiredCount', { count: equipType.quantidade, unidade: equipType.unidade }) }}/s)</span>
                      <v-spacer></v-spacer>
                      <v-chip small color="primary" v-if="equipType.inventariavel === 1 && createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento] && getRealEquipmentCount(createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento]) > 0">
                        {{ getRealEquipmentCount(createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento]) }} {{ $t('equipment.kit.equipmentsSelected') }}
                      </v-chip>
                      <v-chip small color="success" v-else-if="equipType.inventariavel === 0 && isNonControlledChecked(equipType)">
                        {{ $t('equipment.kit.confirmPresence') }}
                      </v-chip>
                    </v-card-title>
                    <v-card-text>
                      <!-- Non-inventoriavel (equipment type): checkbox only (no selection) -->
                      <v-checkbox
                        v-if="equipType.inventariavel === 0"
                        :input-value="isNonControlledChecked(equipType)"
                        :label="$t('equipment.kit.confirmPresence')"
                        hide-details
                        @change="toggleNonControlled(equipType, $event)">
                      </v-checkbox>
                      <!-- Inventoriavel (equipment type): autocomplete to select specific equipment -->
                      <v-autocomplete
                        v-else
                        v-model="createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento]"
                        :items="getEquipamentosByTipo(equipType.id_tipo_equipamento)"
                        item-text="codigo_qrcode"
                        item-value="id_equipamento"
                        :label="equipType.quantidade != null ? $t('equipment.kit.selectEquipmentForTypeCount', { type: equipType.nome_tipo_equipamento, count: equipType.quantidade }) : $t('equipment.kit.selectEquipmentForType', { type: equipType.nome_tipo_equipamento })"
                        multiple
                        chips
                        deletable-chips
                        clearable
                        :filter="(item, queryText) => {
                          const code = item.codigo_qrcode ? item.codigo_qrcode.toLowerCase() : '';
                          const cod = item.cod_equipamento ? item.cod_equipamento.toLowerCase() : '';
                          const searchText = queryText.toLowerCase();
                          return code.includes(searchText) || cod.includes(searchText);
                        }">
                        <template v-slot:append-outer>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-on="on"
                                icon
                                small
                                color="primary"
                                @click="openEquipmentQRScanner(equipType)">
                                <v-icon>mdi-qrcode-scan</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t('equipment.kit.qrScanner.scanEquipment') }}</span>
                          </v-tooltip>
                        </template>
                        <template v-slot:selection="{ item, index }">
                          <v-chip
                            v-if="index < 2"
                            small
                            color="primary"
                            text-color="white">
                            {{ item.codigo_qrcode }}
                          </v-chip>
                          <span
                            v-else-if="index === 2"
                            class="grey--text text-caption">
                            (+{{ getRealEquipmentCount(createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento]) - 2 }} {{ $t('equipment.kit.others') }})
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.codigo_qrcode }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.cod_equipamento">{{ item.cod_equipamento }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeCreateKitDialog">
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn color="primary" @click="saveCreateKit" :disabled="!createKitFormValidado || savingCreateKit" :loading="savingCreateKit">
            {{ $t('button.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog (status-kit) -->
    <v-dialog v-model="confirmDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">
          <span style="color: #d60000">{{ $t('system.alert') }}</span>
        </v-card-title>
        <v-card-text>
          <span style="color: #d60000">{{ mensagemdialog }}</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red accent-4" class="white--text" @click="confirmDialog = false">{{ messageDismissButton }}</v-btn>
          <v-btn v-if="showConfirmButton && confirmDialogType === 'status-kit'" color="primary" class="white--text" @click="confirmKitStatusUpdate">{{ messageConfirmButton }}</v-btn>
          <v-btn v-if="showConfirmButton && confirmDialogType === 'delete-kit'" color="primary" class="white--text" @click="confirmKitDelete" :loading="deletingKit">{{ messageConfirmButton }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kits Table -->
    <merus-data-table :headers="kitHeaders" :items="kits" :loading="loadingKitsTable" :default-multi-sort="kitDefaultSort"
      :loading-text="$t('equipment.kit.loading')"

 cod-tabela="KITS" @update:headers="kitHeaders = $event">
      <template v-slot:toolbar>
        <CadastroToolbar :title="$t('equipment.kit.title')" :token="token" :items="kits"
          :csv-title="$t('equipment.kit.title')" :pdf-title="$t('equipment.kit.title')" :pdf-headers="kitHeaders"
          :pdf-filename="$t('equipment.kit.title')" hide-search>
          <template v-slot:add>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" color="primary" dark small class="mb-2 mr-2" fab @click="openDownloadKitQRDialog">
                  <v-icon>mdi-qrcode</v-icon>
                </v-btn>
              </template>
                <span>{{ $t('equipment.qrcode.downloadTitle') }}</span>
            </v-tooltip>
            <v-btn color="primary" dark small class="mb-2 mr-2" @click="createKitDialog = true" fab>
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
        </CadastroToolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" color="primary" @click="updateKitStatus(item)">mdi-progress-check</v-icon>
        <v-icon small class="mr-2" color="primary" @click="downloadIndividualKitQRCode(item)" :title="$t('equipment.kit.qrcode.downloadIndividual')">mdi-qrcode</v-icon>
        <v-icon small class="mr-2" color="green" @click="editKit(item)">mdi-pencil</v-icon>
        <v-icon small class="mr-2" color="red" @click="deleteKit(item)">mdi-delete</v-icon>
      </template>
    </merus-data-table>

    <!-- QR Scanner Dialog -->
    <v-dialog v-model="qrScannerDialog" max-width="800" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>
            {{ qrScannerMode === 'equipment' ? $t('equipment.kit.qrScanner.scanEquipmentTitle') : $t('equipment.qrScanner.title') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeQRScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-alert v-if="qrScannerError" type="error" dense class="mb-4">
              {{ qrScannerError }}
            </v-alert>
            <v-alert type="info" dense class="mb-4">
              {{
                qrScannerMode === 'equipment'
                  ? $t('equipment.kit.qrScanner.instructionsEquipment')
                  : $t('equipment.qrScanner.instructions')
              }}
            </v-alert>
            <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto;">
              <qrcode-stream
                v-if="qrScannerDialog"
                @decode="onQRDecode"
                @init="onQRInit"
                :camera="qrCamera"
                style="width: 100%; border-radius: 8px;"
              ></qrcode-stream>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeQRScanner">
            {{ $t('button.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DownloadQRDialog v-model="downloadQRDialogOpen" :token="token" />
  </v-container>
</template>

<script>

import apiController from "@/utils/apiController";
import { validador } from "@/constants";
import MerusDataTable from "./Tables/MerusDataTable.vue";
import CadastroToolbar from "./Toolbars/CadastroToolbar.vue";
import EventBus from "@/utils/eventBus";
import { QrcodeStream } from "vue-qrcode-reader";
import DownloadQRDialog from "./Dialogs/DownloadQRDialog.vue";

export default {
  name: "CRUDKits",
  components: {
    MerusDataTable,
    CadastroToolbar,
    QrcodeStream,
    DownloadQRDialog
  },
  data: () => ({
    kitHeaders: [],
    token: null,
    showConfirmButton: true,
    kitDialog: false,
    kitFormValidado: true,
    savingKit: false,
    kitIsAtivo: true,
    confirmDialog: false,
    confirmDialogType: "status-kit",
    mensagemdialog: "",
    nextStatusInfo: null,
    loadingKitsTable: true,
    equipamentos: [],
    kits: [],
    veiculos: [],
    empresas: [],
    tiposequipamentos: [],
    statusKits: [],
    kitItem: {
      nome_kit: null,
      id_empresa: null,
      ativo: 1,
      equipmentTypeRows: [{ id_tipo_equipamento: null, quantidade: 1, controla_inventario: 1 }],
    },
    defaultKitItem: {
      nome_kit: null,
      id_empresa: null,
      ativo: 1,
      equipmentTypeRows: [{ id_tipo_equipamento: null, quantidade: 1, controla_inventario: 1 }],
    },
    createKitDialog: false,
    createKitFormValidado: true,
    savingCreateKit: false,
    createKitIsAtivo: true,
    editedKitIndex: -1,
    equipmentIdsInKits: [],
    tiposKits: [],
    selectedKitTypeEquipmentTypes: [],
    createKitItem: {
      id_kit: null,
      id_tipo_kit: null,
      id_empresa: null,
      id_veiculo: null,
      cod_kit: null,
      nome_kit: null,
      ativo: 1,
      id_status_kit: 0,
      descricao_status_kit: null,
      selectedEquipamentos: {},
    },
    defaultCreateKitItem: {
      id_kit: null,
      id_tipo_kit: null,
      id_empresa: null,
      id_veiculo: null,
      cod_kit: null,
      nome_kit: null,
      ativo: 1,
      id_status_kit: 0,
      descricao_status_kit: null,
      selectedEquipamentos: {},
    },
    qrScannerDialog: false,
    qrScannerError: null,
    qrScannerMode: "kit",
    qrScannerTipoEquipamentoId: null,
    qrCamera: "auto",
    downloadQRDialogOpen: false,
    downloadingIndividualKitQRCode: false,
    availableKitQRCodes: [],
    kitToDelete: null,
    deletingKit: false,
  }),
  computed: {
    createKitFormTitle() {
      return this.editedKitIndex === -1 ? this.$t("equipment.kit.createKit") : this.$t("equipment.kit.editKit");
    },
    messageConfirmButton() {
      return this.$t("button.confirm");
    },
    messageDismissButton() {
      return this.$t("button.dismiss");
    },
    kitDefaultSort() {
      return [
        { value: "cod_kit", options: { text: this.$t("routs.increasing"), value: 1 } }];
    },
    codKitAutocompleteItems() {
      const available = this.availableKitQRCodes || [];
      const current = this.createKitItem.cod_kit;
      if (this.editedKitIndex >= 0 && current) {
        const exists = available.some((item) => (item.codigo || item) === current);
        if (!exists) {
          return [{ codigo: current }, ...available];
        }
      }
      return available;
    },
    /** Controlled equipment types that have no available items in the autocomplete */
    equipmentTypesWithNoAvailableItems() {
      if (!this.selectedKitTypeEquipmentTypes || this.selectedKitTypeEquipmentTypes.length === 0) return [];
      return this.selectedKitTypeEquipmentTypes.filter((et) => {
        if (et.inventariavel !== 1) return false;
        const items = this.getEquipamentosByTipo(et.id_tipo_equipamento);
        return !items || items.length === 0;
      });
    },
    veiculosParaKit() {
      if (!this.veiculos || this.veiculos.length === 0) return [];
      if (!this.createKitItem.id_empresa) return this.veiculos;
      return this.veiculos.filter((v) => Number(v.id_empresa) === Number(this.createKitItem.id_empresa));
    },
  },
  watch: {
    createKitDialog(val) {
      if (val) {
        if (this.editedKitIndex === -1) {
          this.createKitItem = Object.assign({}, this.defaultCreateKitItem);
          this.createKitItem.selectedEquipamentos = {};
          this.selectedKitTypeEquipmentTypes = [];
        }
        this.loadEquipmentIdsInKits();
        this.loadAvailableKitQRCodes();
        this.$nextTick(() => this.applySingleEmpresaIfOnlyOption());
      } else {
        this.closeCreateKitDialog();
      }
    },
    empresas() {
      if (this.createKitDialog && this.editedKitIndex === -1) {
        this.$nextTick(() => this.applySingleEmpresaIfOnlyOption());
      }
    },
  },
  created() {
    this.token = sessionStorage.getItem("api");
    this.populaEmpresas();
    this.populaTiposEquipamentos();
    this.populaEquipamentos();
    this.populaStatusKits();
    this.populaTiposKits();
    this.populaVeiculos();
    this.populaKits();
  },
  methods: {

    applySingleEmpresaIfOnlyOption() {
      if (!this.createKitDialog || this.editedKitIndex !== -1) {
        return;
      }
      if (this.createKitItem.id_empresa != null && this.createKitItem.id_empresa !== '') {
        return;
      }
      if (!Array.isArray(this.empresas) || this.empresas.length !== 1) {
        return;
      }
      this.createKitItem.id_empresa = this.empresas[0].id_empresa;
    },
    populaEmpresas() {
      apiController.request(apiController.endpoints.getallEmpresas, {
        onSuccess: (response) => {
          this.empresas = response.data;
          this.$nextTick(() => this.applySingleEmpresaIfOnlyOption());
        },
      });
    },
    populaTiposEquipamentos() {
      apiController.request(apiController.endpoints.getallTiposequipamentos, {
        onSuccess: (response) => {
          this.tiposequipamentos = response.data;
        },
      });
    },
    populaEquipamentos() {
      apiController.request(apiController.endpoints.getallEquipamentos, {
        onSuccess: (response) => {
          this.equipamentos = response.data;
        },
      });
    },
    populaStatusKits() {
      apiController.request(apiController.endpoints.getallStatuskit, {
        onSuccess: (response) => {
          this.statusKits = response.data;
        },
      });
    },
    populaVeiculos() {
      apiController.request(apiController.endpoints.getallVeiculosativos, {
        onSuccess: (response) => {
          this.veiculos = response.data || [];
        },
      });
    },
    populaKits() {
      apiController.request(apiController.endpoints.getallKits, {
        onSuccess: (response) => {
          this.kits = response.data;
          this.loadingKitsTable = false;
        },
        onError: () => {
          this.loadingKitsTable = false;
        },
      });
    },
    createKitCodeRules() {
      if (!validador(this.createKitItem.cod_kit, 1, 30, 0, 1)) {
        return this.$t("equipment.kit.codeRequired");
      }
      return true;
    },
    getAvailableTiposForRow(rowIndex) {
      let rows = this.kitItem.equipmentTypeRows || [];
      const selectedIds = rows
        .map((r, i) => (i !== rowIndex ? r.id_tipo_equipamento : null))
        .filter((id) => id != null);
      if (!this.tiposequipamentos || !this.tiposequipamentos.length) return [];
      return this.tiposequipamentos.filter(
        (t) => !selectedIds.includes(t.id_tipo_equipamento)
      );
    },
    addKitTypeRow() {
      this.kitItem.equipmentTypeRows.push({ id_tipo_equipamento: null, quantidade: 1, controla_inventario: 1 });
    },
    removeKitTypeRow(index) {
      if (this.kitItem.equipmentTypeRows.length <= 1) return;
      this.kitItem.equipmentTypeRows.splice(index, 1);
    },
    closeKitDialog() {
      this.kitDialog = false;
      this.$nextTick(() => {
        this.kitItem = Object.assign({}, this.defaultKitItem);
        this.kitItem.equipmentTypeRows = [{ id_tipo_equipamento: null, quantidade: 1, controla_inventario: 1 }];
        this.kitIsAtivo = true;
        if (this.$refs.kitForm) {
          this.$refs.kitForm.resetValidation();
        }
      });
    },
    saveKit() {
      this.$refs.kitForm.validate();
      if (this.kitFormValidado) {
        if (this.kitIsAtivo) {
          this.kitItem.ativo = 1;
        } else {
          this.kitItem.ativo = 0;
        }
        let rowsWithType = (this.kitItem.equipmentTypeRows || []).filter(
          (r) => r.id_tipo_equipamento != null
        );
        if (rowsWithType.length === 0) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.equipmentTypesRequired"),
            tipo: "error",
          });
          return;
        }
        const kitTypeData = {
          nome_kit: this.kitItem.nome_kit,
          id_empresa: this.kitItem.id_empresa,
          ativo: this.kitItem.ativo,
          tipos_equipamentos: rowsWithType.map((r) => ({
            id_tipo_equipamento: r.id_tipo_equipamento,
            quantidade: r.quantidade != null && r.quantidade >= 1 ? r.quantidade : 1,
            controla_inventario: r.controla_inventario === 1 ? 1 : 0,
          })),
        };
        this.savingKit = true;
        apiController.request(apiController.endpoints.insertKittipo, {
          data: kitTypeData,
          onSuccess: (response) => {
            this.savingKit = false;
            if (response.data.erro) {
              EventBus.$emit("show-alert", {
                mensagem: response.data.mensagem || this.$t("equipment.kit.error.create"),
                tipo: "error",
              });
            } else {
              EventBus.$emit("show-alert", {
                mensagem: this.$t("equipment.kit.success.created"),
                tipo: "success",
              });
              this.closeKitDialog();
              this.populaTiposKits();
            }
          },
          onError: () => {
            this.savingKit = false;
          },
        });
      }
    },
    populaTiposKits() {
      apiController.request(apiController.endpoints.getallTiposkits, {
        onSuccess: (response) => {
          this.tiposKits = response.data;
        },
      });
    },
    async onKitTypeSelected(idTipoKit) {
      if (!idTipoKit) {
        this.selectedKitTypeEquipmentTypes = [];
        this.createKitItem.selectedEquipamentos = {};
        return;
      }
      try {
        const response = await apiController.request(apiController.endpoints.getTipokitbyid, {
          params: { id: idTipoKit },
          silent: true,
        });
        {
          const tipoKit = response.data;
          if (tipoKit.tipos_equipamentos_ids && tipoKit.tipos_equipamentos_ids.length > 0) {
            const quantidades = tipoKit.tipos_equipamentos_quantidades || [];
            const controlaInventario = tipoKit.tipos_equipamentos_controla_inventario || [];
            this.selectedKitTypeEquipmentTypes = tipoKit.tipos_equipamentos_ids.map((id, index) => {
              const idTipoEquipamento = typeof id === "number" ? id : parseInt(id, 10);
              const tipo = (this.tiposequipamentos || []).find((t) => {
                const tipoId = typeof t.id_tipo_equipamento === "number" ? t.id_tipo_equipamento : parseInt(t.id_tipo_equipamento, 10);
                return tipoId === idTipoEquipamento;
              });
              const inventariavel = tipo && (tipo.inventariavel === 1 || tipo.inventariavel === "1") ? 1 : 0;
              return {
                id_tipo_equipamento: idTipoEquipamento,
                nome_tipo_equipamento: tipoKit.tipos_equipamentos_nomes[index],
                quantidade: quantidades[index] != null && quantidades[index] >= 1 ? quantidades[index] : 1,
                controla_inventario: controlaInventario[index] === 1 ? 1 : 0,
                inventariavel,
                unidade: tipo && tipo.nome_unidade_equipamento ? tipo.nome_unidade_equipamento : "",
              };
            });
            this.selectedKitTypeEquipmentTypes.forEach((equipType) => {
              if (!this.createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento]) {
                this.$set(this.createKitItem.selectedEquipamentos, equipType.id_tipo_equipamento, []);
              }
            });
          } else {
            this.selectedKitTypeEquipmentTypes = [];
          }
        }
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
        this.selectedKitTypeEquipmentTypes = [];
      }
    },
    getEquipamentosByTipo(idTipoEquipamento) {
      if (!this.createKitItem.id_empresa) return [];
      const currentKitEquipmentIds =
        this.editedKitIndex >= 0 && this.createKitItem.selectedEquipamentos
          ? Object.values(this.createKitItem.selectedEquipamentos).flat().filter((id) => typeof id === "number" && !isNaN(id))
          : [];
      const equipmentIdsToExclude = this.equipmentIdsInKits.filter((id) => !currentKitEquipmentIds.includes(id));
      return this.equipamentos.filter(
        (eq) =>
          eq.id_tipo_equipamento === idTipoEquipamento &&
          eq.id_empresa === this.createKitItem.id_empresa &&
          (eq.ativo === 1 || eq.ativo === "1") &&
          !equipmentIdsToExclude.includes(eq.id_equipamento)
      );
    },
    /** Count only real equipment IDs (exclude non-controlled placeholders) */
    getRealEquipmentCount(arr) {
      if (!Array.isArray(arr)) return 0;
      return arr.filter((id) => typeof id === "number" && !isNaN(id)).length;
    },
    /** True when this equipment type is non-inventoriavel and user has checked the box */
    isNonControlledChecked(equipType) {
      if (equipType.inventariavel !== 0) return false;
      const arr = this.createKitItem.selectedEquipamentos[equipType.id_tipo_equipamento];
      const qty = equipType.quantidade != null && equipType.quantidade >= 1 ? equipType.quantidade : 1;
      return Array.isArray(arr) && arr.length === qty && arr.every((x) => x == null);
    },
    /** Toggle non-controlled type: checked = placeholder array of length quantidade, unchecked = [] */
    toggleNonControlled(equipType, checked) {
      const qty = equipType.quantidade != null && equipType.quantidade >= 1 ? equipType.quantidade : 1;
      this.$set(
        this.createKitItem.selectedEquipamentos,
        equipType.id_tipo_equipamento,
        checked ? Array(qty).fill(null) : []
      );
    },
    /** Returns expected equipment types that have no equipment (or insufficient) for the current kit form */
    getMissingEquipmentTypesForKit() {
      if (!this.selectedKitTypeEquipmentTypes || this.selectedKitTypeEquipmentTypes.length === 0) return [];
      return this.selectedKitTypeEquipmentTypes.filter((et) => {
        if (et.inventariavel === 0) {
          return !this.isNonControlledChecked(et);
        }
        const count = this.getRealEquipmentCount(this.createKitItem.selectedEquipamentos[et.id_tipo_equipamento] || []);
        const required = et.quantidade != null && et.quantidade >= 1 ? et.quantidade : 1;
        return count < required;
      });
    },
    clearCreateKitFieldsForNew() {
      this.createKitItem.cod_kit = null;
      this.createKitItem.selectedEquipamentos = {};
      this.selectedKitTypeEquipmentTypes.forEach((equipType) => {
        this.$set(this.createKitItem.selectedEquipamentos, equipType.id_tipo_equipamento, []);
      });
      this.loadAvailableKitQRCodes();
    },
    closeCreateKitDialog() {
      this.createKitDialog = false;
      this.$nextTick(() => {
        this.createKitItem = Object.assign({}, this.defaultCreateKitItem);
        this.selectedKitTypeEquipmentTypes = [];
        this.createKitIsAtivo = true;
        this.editedKitIndex = -1;
        if (this.$refs.createKitForm) {
          this.$refs.createKitForm.resetValidation();
        }
      });
    },
    loadEquipmentIdsInKits() {
      const excludeKitId = this.editedKitIndex >= 0 ? this.createKitItem.id_kit : null;
      apiController.request(apiController.endpoints.getEquipmentidsinkits, {
        params: { excludeKitId },
        silent: true,
        onSuccess: (response) => {
          this.equipmentIdsInKits = response.data || [];
        },
        onError: (e) => {
          console.error("Error loading equipment IDs in kits:", e);
          this.equipmentIdsInKits = [];
        },
      });
    },
    async loadAvailableKitQRCodes() {
      try {
        const response = await apiController.request(apiController.endpoints.getAvailableqrcodeskits, {
          silent: true,
        });
        this.availableKitQRCodes = response.data || [];
        if (this.editedKitIndex === -1 && this.availableKitQRCodes.length === 0) {
          await apiController.request(apiController.endpoints.ensureqrcodepool, { silent: true });
          return this.loadAvailableKitQRCodes();
        }
      } catch (e) {
        console.error("Error loading available kit QR codes:", e);
        this.availableKitQRCodes = [];
        EventBus.$emit("show-error", { error: e });
      }
    },
    saveCreateKit() {
      this.$refs.createKitForm.validate();
      if (!this.createKitFormValidado) return;
      let totalEquipamentos = 0;
      Object.keys(this.createKitItem.selectedEquipamentos).forEach((key) => {
        const arr = this.createKitItem.selectedEquipamentos[key];
        if (arr && arr.length > 0) {
          totalEquipamentos += arr.length;
        }
      });
      const missingTypes = this.getMissingEquipmentTypesForKit();
      if (missingTypes.length > 0) {
        const tipoKit = this.tiposKits.find((t) => t.id_tipo_kit === this.createKitItem.id_tipo_kit);
        EventBus.$emit("kit-missing-equipment", {
          id_tipo_kit: this.createKitItem.id_tipo_kit,
          nome_tipo_kit: tipoKit ? tipoKit.nome_kit : null,
          missingTypes: missingTypes.map((et) => ({
            id_tipo_equipamento: et.id_tipo_equipamento,
            nome_tipo_equipamento: et.nome_tipo_equipamento,
            quantidade: et.quantidade != null && et.quantidade >= 1 ? et.quantidade : 1,
            controla_inventario: et.controla_inventario,
          })),
        });
      }
      if (totalEquipamentos === 0) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.equipmentTypesRequired"),
          tipo: "error",
        });
        return;
      }
      const equipTypeWithQty = this.selectedKitTypeEquipmentTypes.find((et) => {
        const arr = this.createKitItem.selectedEquipamentos[et.id_tipo_equipamento] || [];
        const qty = et.quantidade != null && et.quantidade >= 1 ? et.quantidade : 1;
        return arr.length !== qty;
      });
      if (equipTypeWithQty) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.requiredCountMismatch", {
            type: equipTypeWithQty.nome_tipo_equipamento,
            count: equipTypeWithQty.quantidade,
          }),
          tipo: "error",
        });
        return;
      }
      if (this.createKitIsAtivo) this.createKitItem.ativo = 1;
      else this.createKitItem.ativo = 0;
      const kitData = {
        id_tipo_kit: this.createKitItem.id_tipo_kit,
        id_empresa: this.createKitItem.id_empresa,
        id_veiculo: this.createKitItem.id_veiculo || undefined,
        cod_kit: this.createKitItem.cod_kit,
        nome_kit: this.createKitItem.nome_kit || null,
        ativo: this.createKitItem.ativo,
        equipamentos: [],
        equipamentos_nao_controlados: [],
      };
      Object.keys(this.createKitItem.selectedEquipamentos).forEach((key) => {
        const arr = this.createKitItem.selectedEquipamentos[key] || [];
        const realIds = arr.filter((id) => typeof id === "number" && !isNaN(id));
        if (realIds.length > 0) {
          kitData.equipamentos = kitData.equipamentos.concat(realIds);
        }
      });
      this.selectedKitTypeEquipmentTypes.forEach((equipType) => {
        if (equipType.inventariavel === 0 && this.isNonControlledChecked(equipType)) {
          kitData.equipamentos_nao_controlados.push({
            id_tipo_equipamento: equipType.id_tipo_equipamento,
            quantidade: equipType.quantidade != null && equipType.quantidade >= 1 ? equipType.quantidade : 1,
          });
        }
      });
      this.savingCreateKit = true;
      if (this.editedKitIndex === -1) {
        apiController.request(apiController.endpoints.insertKit, {
          data: kitData,
          onSuccess: (response) => {
            this.savingCreateKit = false;
            if (response.data.erro) {
              EventBus.$emit("show-alert", {
                mensagem: response.data.mensagem || this.$t("equipment.kit.error.createKit"),
                tipo: "error",
              });
            } else {
              EventBus.$emit("show-alert", {
                mensagem: this.$t("equipment.kit.success.kitCreated"),
                tipo: "success",
              });
              this.populaKits();
              this.clearCreateKitFieldsForNew();
            }
          },
          onError: () => {
            this.savingCreateKit = false;
          },
        });
      } else {
        kitData.id_kit = this.createKitItem.id_kit;
        if (this.createKitItem.id_veiculo) kitData.id_veiculo = this.createKitItem.id_veiculo;
        apiController.request(apiController.endpoints.updateKit, {
          data: kitData,
          onSuccess: (response) => {
            this.savingCreateKit = false;
            if (response.data.erro) {
              EventBus.$emit("show-alert", {
                mensagem: response.data.mensagem || this.$t("equipment.kit.error.updateKit"),
                tipo: "error",
              });
            } else {
              EventBus.$emit("show-alert", {
                mensagem: this.$t("equipment.kit.success.kitUpdated"),
                tipo: "success",
              });
              this.closeCreateKitDialog();
              this.populaKits();
            }
          },
          onError: () => {
            this.savingCreateKit = false;
          },
        });
      }
    },
    editKit(item) {
      this.editedKitIndex = this.kits.indexOf(item);
      apiController.request(apiController.endpoints.getKitbyid, {
        params: { id: item.id_kit },
        onSuccess: (response) => {
          if (response.data.erro) {
            EventBus.$emit("show-alert", {
              mensagem: response.data.mensagem || this.$t("equipment.kit.error.loadKit"),
              tipo: "error",
            });
            return;
          }
          const kitData = response.data;
          this.createKitItem = {
            id_kit: kitData.id_kit,
            id_tipo_kit: kitData.id_tipo_kit,
            id_empresa: kitData.id_empresa,
            id_veiculo: kitData.id_veiculo || null,
            cod_kit: kitData.cod_kit,
            nome_kit: kitData.nome_kit || null,
            ativo: kitData.ativo,
            id_status_kit: kitData.id_status_kit || 0,
            descricao_status_kit: kitData.descricao_status_kit || null,
            selectedEquipamentos: {},
          };
          this.createKitIsAtivo = kitData.ativo === 1;
          if (kitData.id_tipo_kit) {
            this.onKitTypeSelected(kitData.id_tipo_kit)
              .then(() => {
                if (kitData.equipamentos && kitData.equipamentos.length > 0) {
                  kitData.equipamentos.forEach((equip) => {
                    const tipoId = equip.id_tipo_equipamento;
                    if (!this.createKitItem.selectedEquipamentos[tipoId]) {
                      this.$set(this.createKitItem.selectedEquipamentos, tipoId, []);
                    }
                    this.createKitItem.selectedEquipamentos[tipoId].push(equip.id_equipamento);
                  });
                }
                if (kitData.equipamentos_nao_controlados && kitData.equipamentos_nao_controlados.length > 0) {
                  kitData.equipamentos_nao_controlados.forEach((item) => {
                    const qty = item.quantidade != null && item.quantidade >= 1 ? item.quantidade : 1;
                    this.$set(this.createKitItem.selectedEquipamentos, item.id_tipo_equipamento, Array(qty).fill(null));
                  });
                }
                this.loadEquipmentIdsInKits();
                this.createKitDialog = true;
              })
              .catch((e) => EventBus.$emit("show-error", { error: e }));
          } else {
            this.loadEquipmentIdsInKits();
            this.createKitDialog = true;
          }
        },
      });
    },
    deleteKit(item) {
      this.kitToDelete = item;
      this.mensagemdialog = this.$t("equipment.kit.confirmDelete", { name: item.cod_kit || item.nome_kit || item.id_kit });
      this.showConfirmButton = true;
      this.confirmDialogType = "delete-kit";
      this.confirmDialog = true;
    },
    confirmKitDelete() {
      if (!this.kitToDelete || !this.kitToDelete.id_kit) {
        this.confirmDialog = false;
        this.kitToDelete = null;
        return;
      }
      this.deletingKit = true;
      apiController.request(apiController.endpoints.deleteKit, {
        params: { id: this.kitToDelete.id_kit },
        onSuccess: (response) => {
          this.deletingKit = false;
          this.confirmDialog = false;
          this.kitToDelete = null;
          if (response.data.erro) {
            EventBus.$emit("show-alert", {
              mensagem: response.data.mensagem || this.$t("equipment.kit.error.deleteKit"),
              tipo: "error",
            });
          } else {
            EventBus.$emit("show-alert", {
              mensagem: this.$t("equipment.kit.success.kitDeactivated"),
              tipo: "success",
            });
            this.populaKits();
          }
        },
        onError: () => {
          this.deletingKit = false;
        },
      });
    },
    updateKitStatus(item) {
      if (this.statusKits.length === 0) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.updateTable.error.noStatusAvailable"),
          tipo: "error",
        });
        return;
      }
      const currentStatusId = item.id_status_kit || 0;
      const currentIndex = this.statusKits.findIndex((status) => status.id_status_kit === currentStatusId);
      const nextIndex =
        currentIndex === -1 || currentIndex === this.statusKits.length - 1 ? 0 : currentIndex + 1;
      const nextStatus = this.statusKits[nextIndex];
      const currentStatus = currentIndex >= 0 ? this.statusKits[currentIndex] : null;
      this.createKitItem = Object.assign({}, item);
      this.nextStatusInfo = { nextStatus, currentStatus };
      this.mensagemdialog = this.$t("equipment.kit.confirmStatusUpdate", {
        kit: item.cod_kit,
        current: currentStatus ? currentStatus.descricao_status_kit : this.$t("equipment.noStatus"),
        next: nextStatus.descricao_status_kit,
      });
      this.showConfirmButton = true;
      this.confirmDialogType = "status-kit";
      this.messageDismissButton = this.$t("button.dismiss");
      this.messageConfirmButton = this.$t("button.confirm");
      this.confirmDialog = true;
    },
    confirmKitStatusUpdate() {
      if (!this.nextStatusInfo) {
        this.confirmDialog = false;
        return;
      }
      this.confirmDialog = false;
      const nextStatusInfo = this.nextStatusInfo;
      this.nextStatusInfo = null;
      const kitData = {
        id_kit: this.createKitItem.id_kit,
        id_tipo_kit: this.createKitItem.id_tipo_kit,
        id_empresa: this.createKitItem.id_empresa,
        cod_kit: this.createKitItem.cod_kit,
        nome_kit: this.createKitItem.nome_kit || null,
        ativo: this.createKitItem.ativo,
        id_status_kit: nextStatusInfo.nextStatus.id_status_kit,
        // Omit equipamentos so backend leaves existing associations unchanged (status-only update)
      };
      apiController.request(apiController.endpoints.updateKit, {
        data: kitData,
        onSuccess: (response) => {
          if (response.data.erro) {
            EventBus.$emit("show-alert", {
              mensagem: response.data.mensagem || this.$t("equipment.kit.error.updateKit"),
              tipo: "error",
            });
          } else {
            EventBus.$emit("show-alert", {
              mensagem: this.$t("equipment.kit.success.statusUpdated", { kit: this.createKitItem.cod_kit, next: nextStatusInfo.nextStatus.descricao_status_kit }),
              tipo: "success",
            });
            this.populaKits();
          }
        },
      });
    },
    openKitQRScanner() {
      this.qrScannerMode = "kit";
      this.qrScannerTipoEquipamentoId = null;
      this.qrScannerError = null;
      this.qrScannerDialog = true;
    },
    openEquipmentQRScanner(equipType) {
      if (!this.createKitItem.id_empresa) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.qrScanner.selectCompanyFirst"),
          tipo: "error",
        });
        return;
      }
      if (!equipType || equipType.inventariavel !== 1) return;
      this.qrScannerMode = "equipment";
      this.qrScannerTipoEquipamentoId = equipType.id_tipo_equipamento;
      this.qrScannerError = null;
      this.qrScannerDialog = true;
    },
    closeQRScanner() {
      this.qrScannerDialog = false;
      this.qrScannerError = null;
      this.qrCamera = "auto";
      this.qrScannerMode = "kit";
      this.qrScannerTipoEquipamentoId = null;
    },
    extractCodeFromQR(decodedString) {
      const s = (decodedString || "").trim();
      if (!s) return "";
      try {
        const j = JSON.parse(s);
        if (j && typeof j === "object" && !Array.isArray(j)) {
          if (j.codeEquip != null && String(j.codeEquip).trim() !== "") return String(j.codeEquip).trim();
          if (j.codigo_qrcode != null && String(j.codigo_qrcode).trim() !== "") return String(j.codigo_qrcode).trim();
          if (j.codigo != null && String(j.codigo).trim() !== "") return String(j.codigo).trim();
        }
      } catch (e) {
        /* plain text */
      }
      return s;
    },
    findEquipmentByScannedCodeForKitType(idTipo, rawDecoded) {
      const code = this.extractCodeFromQR(rawDecoded);
      if (!code) return null;
      const c = code.trim().toLowerCase();
      const items = this.getEquipamentosByTipo(idTipo);
      return (
        items.find((e) => {
          const cq = e.codigo_qrcode != null ? String(e.codigo_qrcode).trim().toLowerCase() : "";
          const ce = e.cod_equipamento != null ? String(e.cod_equipamento).trim().toLowerCase() : "";
          return cq === c || ce === c;
        }) || null
      );
    },
    onQRDecode(decodedString) {
      if (this.qrScannerMode === "equipment") {
        const idTipo = this.qrScannerTipoEquipamentoId;
        if (idTipo == null) {
          this.closeQRScanner();
          return;
        }
        const eq = this.findEquipmentByScannedCodeForKitType(idTipo, decodedString);
        if (!eq) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.qrScanner.equipmentNotFound"),
            tipo: "error",
          });
          this.closeQRScanner();
          return;
        }
        const equipType = this.selectedKitTypeEquipmentTypes.find((t) => t.id_tipo_equipamento === idTipo);
        const max =
          equipType && equipType.quantidade != null && equipType.quantidade >= 1 ? equipType.quantidade : 1;
        let arr = this.createKitItem.selectedEquipamentos[idTipo];
        if (!Array.isArray(arr)) {
          arr = [];
          this.$set(this.createKitItem.selectedEquipamentos, idTipo, arr);
        }
        if (arr.includes(eq.id_equipamento)) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.qrScanner.equipmentAlreadySelected"),
            tipo: "warning",
          });
          this.closeQRScanner();
          return;
        }
        const count = this.getRealEquipmentCount(arr);
        if (count >= max) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.qrScanner.maxEquipmentsForType"),
            tipo: "error",
          });
          this.closeQRScanner();
          return;
        }
        this.$set(this.createKitItem.selectedEquipamentos, idTipo, arr.concat(eq.id_equipamento));
        this.closeQRScanner();
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.qrScanner.equipmentAdded"),
          tipo: "success",
        });
        return;
      }
      this.createKitItem.cod_kit = (decodedString || "").trim();
      this.closeQRScanner();
      EventBus.$emit("show-alert", {
        mensagem: this.$t("equipment.kit.qrScanner.success"),
        tipo: "success",
      });
    },
    onQRInit(promise) {
      promise
        .then(() => {
          this.qrScannerError = null;
        })
        .catch((error) => {
          let errorMessage = this.$t("equipment.qrScanner.cameraError");
          if (error.name === "NotAllowedError") errorMessage = this.$t("equipment.qrScanner.cameraPermissionDenied");
          else if (error.name === "NotFoundError") errorMessage = this.$t("equipment.qrScanner.cameraNotFound");
          else if (error.name === "NotReadableError") errorMessage = this.$t("equipment.qrScanner.cameraNotReadable");
          else if (error.name === "OverconstrainedError") errorMessage = this.$t("equipment.qrScanner.cameraConstraints");
          this.qrScannerError = errorMessage;
          EventBus.$emit("show-alert", { mensagem: errorMessage, tipo: "error" });
        });
    },
    openDownloadKitQRDialog() {
      this.downloadQRDialogOpen = true;
    },
    closeDownloadKitQRDialog() {
      this.downloadKitQRDialog = false;
      this.kitQRCodeQuantity = 1;
      this.kitQRCodeColumns = 3;
      if (this.$refs.downloadKitQRForm) this.$refs.downloadKitQRForm.resetValidation();
    },
    kitQRCodeQuantityRules() {
      return [
        (v) => !!v || this.$t("equipment.kit.qrcode.quantityRequired"),
        (v) => (v && v > 0) || this.$t("equipment.kit.qrcode.quantityMin"),
        (v) => (v && Number.isInteger(v)) || this.$t("equipment.kit.qrcode.quantityInteger")];
    },
    kitQRCodeColumnsRules() {
      return [
        (v) => v == null || v === '' || (Number.isInteger(v) && v >= 1 && v <= 8) || this.$t("equipment.kit.qrcode.columnsMin")];
    },
    downloadIndividualKitQRCode(item) {
      if (!item.id_kit) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.qrcode.noKitSelected"),
          tipo: "error",
        });
        return;
      }
      this.downloadingIndividualKitQRCode = true;
      apiController.request(apiController.endpoints.downloadQrcodekit, {
        params: { id_kit: item.id_kit },
        config: { responseType: "blob" },
        silent: true,
        onSuccess: (response) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", (item.qrcode_no || item.cod_kit || "qrcode") + ".png");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.downloadingIndividualKitQRCode = false;
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.qrcode.downloadSuccess"),
            tipo: "success",
          });
        },
        onError: (e) => {
          this.downloadingIndividualKitQRCode = false;
          if (e.response && e.response.data instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result);
                EventBus.$emit("show-alert", {
                  mensagem: errorData.mensagem || this.$t("equipment.kit.qrcode.downloadError"),
                  tipo: "error",
                });
              } catch {
                EventBus.$emit("show-alert", {
                  mensagem: this.$t("equipment.kit.qrcode.downloadError"),
                  tipo: "error",
                });
              }
            };
            reader.readAsText(e.response.data);
          } else {
            EventBus.$emit("show-alert", {
              mensagem: (e.response && e.response.data && e.response.data.mensagem) || this.$t("equipment.kit.qrcode.downloadError"),
              tipo: "error",
            });
          }
        },
      });
    },
    downloadKitQRCodes() {
      this.$refs.downloadKitQRForm.validate();
      if (!this.downloadKitQRFormValidado) return;
      if (!this.kitQRCodeQuantity || this.kitQRCodeQuantity < 1) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("equipment.kit.qrcode.invalidQuantity"),
          tipo: "error",
        });
        return;
      }
      this.downloadingKitQRCodes = true;
      apiController.request(apiController.endpoints.downloadQrcodeskits, {
        query: { quantity: this.kitQRCodeQuantity, format: "sheet", columns: this.kitQRCodeColumns || 3 },
        config: { responseType: "blob" },
        silent: true,
        onSuccess: (response) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `qrcodes_kits_stickers_${Date.now()}.png`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.downloadingKitQRCodes = false;
          this.closeDownloadKitQRDialog();
          EventBus.$emit("show-alert", {
            mensagem: this.$t("equipment.kit.qrcode.downloadSuccess"),
            tipo: "success",
          });
        },
        onError: (e) => {
          this.downloadingKitQRCodes = false;
          if (e.response && e.response.data instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result);
                EventBus.$emit("show-alert", {
                  mensagem: errorData.mensagem || this.$t("equipment.kit.qrcode.downloadError"),
                  tipo: "error",
                });
              } catch {
                EventBus.$emit("show-alert", {
                  mensagem: this.$t("equipment.kit.qrcode.downloadError"),
                  tipo: "error",
                });
              }
            };
            reader.readAsText(e.response.data);
          } else {
            EventBus.$emit("show-alert", {
              mensagem: (e.response && e.response.data && e.response.data.mensagem) || this.$t("equipment.kit.qrcode.downloadError"),
              tipo: "error",
            });
          }
        },
      });
    },
  },
};
</script>
